import { useCallback, useRef, useState } from "react";

interface Options {
  /** Number of clicks required to trigger. */
  target?: number;
  /** Clicks reset if the user pauses longer than this (ms). */
  resetAfterMs?: number;
  /** Count at which a subtle hint should appear. */
  hintAt?: number;
}

/**
 * Counts rapid repeated clicks on an element and fires once the target is hit.
 * The counter silently resets after a pause so normal usage never triggers it.
 */
export function useEasterEggCounter(onUnlock: () => void, options: Options = {}) {
  const { target = 16, resetAfterMs = 1500, hintAt = 12 } = options;
  const [count, setCount] = useState(0);
  const lastClick = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const reset = useCallback(() => {
    setCount(0);
    lastClick.current = 0;
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const registerClick = useCallback(() => {
    const now = Date.now();
    const fresh = now - lastClick.current > resetAfterMs;
    lastClick.current = now;

    setCount((prev) => {
      const next = fresh ? 1 : prev + 1;
      if (next >= target) {
        // defer so we never call setState during another component's render
        setTimeout(onUnlock, 0);
        return 0;
      }
      return next;
    });

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCount(0), resetAfterMs);
  }, [onUnlock, resetAfterMs, target]);

  return { registerClick, reset, count, showHint: count >= hintAt };
}
